import { CircularProgress, makeStyles, Paper, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties } from 'react';
import { EntityUIComponentCollection } from '..';
import { useSelectEntity } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue } from '../../../utils/utils';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            position      : 'relative',
            overflow      : 'hidden',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            '&.clickable' : {
                cursor: 'pointer',
            },
        },

        image: {
            width    : '100%',
            maxHeight: 500,
            objectFit: 'cover',
            zIndex   : 1,
        },

        icon: {
            position    : 'absolute',
            zIndex      : 2,
            top         : theme.spacing(1),
            left        : theme.spacing(1),
            width       : 100,
            height      : 100,
            boxShadow   : theme.shadows[2],
            background  : theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
        },

        [theme.breakpoints.up('md')]: {
            icon: {
                position: 'absolute',
                top     : theme.spacing(1.5),
                left    : theme.spacing(1.5),
            },
        },

        [theme.breakpoints.up('lg')]: {
            icon: {
                position: 'absolute',
                top     : theme.spacing(2),
                left    : theme.spacing(2),
            },
        },
    };
}, { name: 'EntityImageViewUIComponent', index: 1 });


/**
 * Interface for EntityImageViewUIComponent's custom settings. There is not much can be customized for this component other than the
 * CSS for all the HTML element used in this component. We don't know what is the best way to display the images because they are all
 * different therefore we will make them all stylable, the root element, the icon element, and the image element.
 */
export interface EntityImageViewUIComponentCustomSettings {
    readonly showIcon?: boolean | undefined;
    readonly containerStyle?: CSSProperties | undefined;
    readonly iconStyle?: CSSProperties | undefined;
    readonly imageStyle?: CSSProperties | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntityImageViewUIComponentCustomSettings
 */
export const isEntityImageViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityImageViewUIComponentCustomSettings => {
    const settings = obj as EntityImageViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showIcon)) {
        return errorLogger?.logError({
            message: '"showIcon" must be undefined or a boolean.',
            value  : settings.showIcon,
            path   : [...configPath, 'showIcon'],
        }) ?? false;
    }

    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    if (settings.iconStyle !== undefined && !isObjectLiteral(settings.iconStyle)) {
        return errorLogger?.logError({
            message: '"iconStyle" must be undefined or a CSSProperties object.',
            value  : settings.iconStyle,
            path   : [...configPath, 'iconStyle'],
        }) ?? false;
    }

    if (settings.imageStyle !== undefined && !isObjectLiteral(settings.imageStyle)) {
        return errorLogger?.logError({
            message: '"imageStyle" must be undefined or a CSSProperties object.',
            value  : settings.imageStyle,
            path   : [...configPath, 'imageStyle'],
        }) ?? false;
    }

    return true;
};


export interface EntityImageViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityImageViewUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's image and icon.
 */
export const EntityImageViewUIComponent: React.FC<EntityImageViewUIComponentProps> = (props: EntityImageViewUIComponentProps) => {

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const classes = useStyle();

    return (
        <>
            {selectedEntity && selectedEntity.displaySettings.imageData?.url && selectedEntity.displaySettings.imageData.url.length > 0 && (
                <Paper
                    className={clsx(props.className, classes.root, props.onClick && 'clickable')}
                    style={{ ...props.style, ...props.customSettings?.containerStyle }}
                    elevation={2}
                    onClick={props.onClick}
                >
                    {props.customSettings?.showIcon && selectedEntity.displaySettings.iconData?.url && (
                        <img
                            className={classes.icon}
                            style={props.customSettings?.iconStyle}
                            src={selectedEntity.displaySettings.iconData?.url}
                            alt={`${selectedEntity.displaySettings.name} Icon`}
                        />
                    )}
                    <img
                        className={classes.image}
                        style={props.customSettings?.imageStyle}
                        src={selectedEntity.displaySettings.imageData?.url}
                        alt={`${selectedEntity.displaySettings.name} Image`}
                    />
                </Paper>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityImageViewUIComponent', EntityImageViewUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Image_Comp',
    {
        componentFunc          : EntityImageViewUIComponent,
        customSettingsValidator: isEntityImageViewUIComponentCustomSettings,
    },
);
